import { useContext, useEffect, useState } from 'react';
import * as L from 'leaflet';
import Swal from 'sweetalert2';
import { Api } from '@services';
import { useMapStore } from '../MapStore/UseMapStore';
import { updateTileLayer, updateBoundsLayer } from '../MapFunctions/MapHelperFunctions';
import { lowercase } from '@utils';
import { ThemeContext } from '@context/ThemeContext';
import {
  disableTransforms,
  enableTransforms,
  isMapAlive,
  isLayerMounted,
} from '../MapFunctions/MapHelperFunctions';

export function useMapManager() {
  const store = useMapStore();
  const { theme } = useContext(ThemeContext);
  const [mapKey, setMapKey] = useState<string | null>(null);

  const {
    mapInstance,
    boundsLayer,
    azureLayer,
    placeLayer,
    gatewayLayer,
    stationaryLayer,
    assetLayer,
    scaleLayer,
    selected,
    mapLayers,
    isStatic,
    boundsFirstEdit,
    setBoundsFirstEdit,
    upgradeInProg,
    edit,
    setEdit,
    URLArr,
    clickEvent,
    newScaleCreated,
    addScale,
    scaleLength,
    setNewScaleCreated,
    createPlace,
    createGateway,
    createStationary,
    createScale,
    placeDrawLayer,
    gatewayDrawLayer,
    stationaryDrawLayer,
    scaleDrawLayer,
    vertexEvent,
    mapUpdated,
    showMore,
    isEditingItem,
    setShowMore,
    setCreatePlace,
    setCreateGateway,
    setCreateStationary,
    setCreateScale,
    mapName,
    mapDescription,
    mapKeywords,
    overlayFC,
    setOverlayFC,
  } = store;

  // ---------------- Fetch Map Server Key ----------------
  useEffect(() => {
    if (!isStatic) {
      Api.get('/me/map-server-key')
        .then((resp) => setMapKey(resp.data as string))
        .catch((err) => console.error('Failed to fetch map key:', err));
    }
  }, [isStatic]);

  // ---------------- Map Edit Toggle ----------------
  useEffect(() => {
    setEdit(URLArr.includes('edit'));
  }, [URLArr, setEdit]);

  // ---------------- Feature Layers Toggle ----------------
  useEffect(() => {
    if (!isMapAlive(mapInstance)) return;

    const layers: { [key: string]: L.Layer | null } = {
      place: placeLayer,
      gateway: gatewayLayer,
      stationary: stationaryLayer,
      asset: assetLayer,
    };

    Object.entries(layers).forEach(([name, layer]) => {
      if (!layer) return;

      const shouldShow = selected === name || mapLayers.includes(name);
      const hasLayer = mapInstance.hasLayer(layer);

      try {
        if (shouldShow && !hasLayer) layer.addTo(mapInstance);
        else if (!shouldShow && hasLayer) mapInstance.removeLayer(layer);
      } catch (err) {
        console.warn(`Layer toggle for "${name}" skipped:`, err);
      }
    });
  }, [mapInstance, placeLayer, gatewayLayer, stationaryLayer, assetLayer, mapLayers, selected]);

  // ---------------- Bounds Layer Toggle & Edit ----------------
  useEffect(() => {
    if (!boundsLayer) return;

    const shouldEdit = selected === 'overlay' && !isStatic && edit;

    if (shouldEdit) {
      if (boundsFirstEdit) {
        Swal.fire({
          allowOutsideClick: false,
          title: 'Important!',
          html: `Adjusting overlay will NOT change Places, Gateways, or Stationary Assets.`,
          icon: 'warning',
          confirmButtonText: 'Okay',
        }).then(() => enableTransforms(boundsLayer));
        setBoundsFirstEdit(false);
      } else enableTransforms(boundsLayer);
    } else disableTransforms(boundsLayer);
  }, [boundsLayer, selected, isStatic, boundsFirstEdit, setBoundsFirstEdit, edit]);

  useEffect(() => {
    if (upgradeInProg && boundsLayer) {
      enableTransforms(boundsLayer);
    }
  }, [upgradeInProg, boundsLayer]);

  // ---------------- Azure & Bounds Styling ----------------
  useEffect(() => {
    if (!isMapAlive(mapInstance)) return;

    const shouldEdit = selected === 'overlay' && !isStatic;

    if (azureLayer && (mapInstance.hasLayer(azureLayer) || isLayerMounted(azureLayer))) {
      const style = mapLayers.includes('sat')
        ? 'microsoft.imagery'
        : theme === 'dark'
        ? 'microsoft.base.darkgrey'
        : 'microsoft.base.road';

      try {
        updateTileLayer(style, azureLayer as any, mapKey || undefined);
      } catch (err) {
        console.warn('updateTileLayer skipped:', err);
      }
    }

    if (boundsLayer && isLayerMounted(boundsLayer)) {
      try {
        updateBoundsLayer(
          mapLayers.includes('edge') || shouldEdit ? 1 : 0,
          boundsLayer as L.GeoJSON,
        );
      } catch (err) {
        console.warn('updateBoundsLayer skipped:', err);
      }
    }
  }, [mapInstance, azureLayer, boundsLayer, selected, isStatic, mapLayers, theme, mapKey]);

  // -------- Disable all other map objects when editing ----------
  useEffect(() => {
    if (!clickEvent) return;

    const clickedId = clickEvent.feature.properties.Id;

    const layerMap = {
      gateway: gatewayLayer,
      stationary: stationaryLayer,
      place: placeLayer,
    };

    const layersToProcess = Object.entries(layerMap)
      .filter(([key]) => mapLayers.includes(key) || selected === key)
      .map(([_, layer]) => layer);

    layersToProcess.forEach((layerGroup) => {
      if (!layerGroup) return;

      layerGroup.eachLayer((layer: any) => {
        const isOtherLayer = layer.feature?.properties?.Id !== clickedId;

        if (isEditingItem) {
          if (isOtherLayer) {
            layer.removeEventListener('click');
          }
        } else {
          if (isOtherLayer) {
            layer.addEventListener('click');
          }
        }
      });
    });
  }, [clickEvent, isEditingItem, mapLayers, selected, gatewayLayer, stationaryLayer, placeLayer]);

  // ---------------- Enable/Disable Drawing Tools ----------------
  useEffect(() => {
    if (!mapInstance) return;

    try {
      if (createPlace && selected === 'place') {
        placeDrawLayer?.enable();
        document.getElementById('myMap')!.style.cursor = 'crosshair';
      } else {
        placeDrawLayer?.disable();
        if (!createScale) document.getElementById('myMap')!.style.cursor = '';
      }

      if (createGateway && selected === 'gateway') gatewayDrawLayer?.enable();
      else gatewayDrawLayer?.disable();
      if (createStationary && selected === 'stationary') stationaryDrawLayer?.enable();
      else stationaryDrawLayer?.disable();
      if (createScale && selected === 'overlay') {
        scaleDrawLayer?.enable();
        document.getElementById('myMap')!.style.cursor = 'crosshair';
      } else {
        scaleDrawLayer?.disable();
        if (!createPlace) document.getElementById('myMap')!.style.cursor = '';
      }
    } catch (err) {
      console.warn('Draw layer toggle skipped:', err);
    }
  }, [
    mapInstance,
    selected,
    createPlace,
    createGateway,
    createStationary,
    createScale,
    placeDrawLayer,
    gatewayDrawLayer,
    stationaryDrawLayer,
    scaleDrawLayer,
  ]);

  // ----- Add scale -----
  useEffect(() => {
    if (!(addScale && newScaleCreated)) return;

    try {
      const scaleFC = newScaleCreated.layers?.toGeoJSON?.();
      const points = scaleFC?.features?.map((f: any) => f.geometry) || [];
      useMapStore.setState({
        scaleLayer: { points, feet: parseFloat(scaleLength) },
        drawEvent: null,
        createScale: false,
        vertexEvent: null,
        newObjCreated: null,
        mapUpdated: true,
      } as any);
    } catch (err) {
      console.warn('Add scale skipped:', err);
    } finally {
      setNewScaleCreated(null);
    }
  }, [addScale, scaleLength, newScaleCreated, setNewScaleCreated]);

  // ---------------- Extra Effects ----------------

  // Limit scale drawing to 2 points
  useEffect(() => {
    if (createScale && vertexEvent) {
      const layerIds = Object.keys(vertexEvent.layers._layers);
      if (layerIds.length > 1) {
        const secondVertex = vertexEvent.layers._layers[layerIds[1]]._icon;
        requestAnimationFrame(() => secondVertex.click());
        setNewScaleCreated(vertexEvent);
      }
    }
  }, [vertexEvent, createScale, setNewScaleCreated]);

  // Sync scale when map updated.
  // This used to also set gatewayFC / placeFC / stationaryFC from layer.toGeoJSON()
  useEffect(() => {
    if (!mapUpdated) return;

    useMapStore.setState({
      scale: scaleLayer,
      mapUpdated: false,
    } as any);
  }, [mapUpdated, scaleLayer]);

  // Turn off drawing control if tab changes
  useEffect(() => {
    if (selected !== 'place') setCreatePlace(false);
    if (selected !== 'gateway') setCreateGateway(false);
    if (selected !== 'stationary') setCreateStationary(false);
    if (selected !== 'overlay') setCreateScale(false);

    // Discard any half-drawn marker/shape left from the previous tab
    const { drawEvent, newObjCreated, removeTempObject } = useMapStore.getState() as any;
    if (drawEvent || newObjCreated) removeTempObject();
  }, [selected, setCreatePlace, setCreateGateway, setCreateStationary, setCreateScale]);

  // Ensure showMore matches selected entity
  useEffect(() => {
    if (!(selected && showMore)) return setShowMore(null);
    if (selected !== lowercase(showMore?.properties.EntityType).split(' ')[0]) setShowMore(null);
  }, [selected, showMore, setShowMore]);

  // Overlay FC metadata sync
  useEffect(() => {
    if (!overlayFC) return;
    const current = overlayFC.features[0]?.properties ?? {};
    if (
      current.Name !== mapName ||
      current.Description !== mapDescription ||
      current.Keywords !== mapKeywords
    ) {
      setOverlayFC({
        ...overlayFC,
        features: [
          {
            ...overlayFC.features[0],
            properties: {
              ...overlayFC.features[0].properties,
              Name: mapName,
              Description: mapDescription,
              Keywords: mapKeywords,
            },
          },
        ],
      });
    }
  }, [mapName, mapDescription, mapKeywords, overlayFC, setOverlayFC]);

  // Click event -> newObj info in store
  useEffect(() => {
    if (!clickEvent) return;
    const { Id, Name, Keywords, Floor } = clickEvent.feature.properties;
    useMapStore.setState({
      newObjId: Id,
      newObjName: Name,
      newObjKeywords: Keywords,
      newObjFloor: Floor,
    } as any);
  }, [clickEvent]);
}
